import React, { useEffect, useState } from "react";
import { Form } from "antd";
import { appTitle } from "../common/GeneralConfigurations";
import QuizBuilderForm from "./QuizBuilderForm";
import { getQuizTypeList } from "../../api/QuizAPI/QuizTypeAPI";
import AdnMessageError from "../common/adnMessage/AdnMessageError";
import {
    addQuiz,
    cloneQuiz,
    getQuizById,
    getQuizList,
    publishQuiz,
    updateQuiz,
} from "../../api/QuizAPI/QuizAPI";
import { getGroupByQuizId } from "../../api/QuizAPI/GroupAPI";
import { useNavigate, useParams } from "react-router-dom";
import AdnMessageSuccess from "../common/adnMessage/AdnMessageSuccess";
import { AppRoutes } from "../../AppRoutes";
import ManageGroupPage from "./ManageGroupPage";
import { getContractTypes } from "../../api/EmployeeApi";

const ManageQuizBuilderPage = () => {
    const navigate = useNavigate();
    const [quizForm] = Form.useForm();
    const { id } = useParams();
    const [showAddGroupButton, setShowAddGroupButton] = useState(false);
    const [showPublishButton, setShowPublishButton] = useState(false);
    const [showGoButton, setGoButton] = useState(false);
    const [quizTypes, setQuizTypes] = useState([]);
    const [contractTypeOptions, setContractTypeOptions] = useState([]);
    const [reference, setReference] = useState("");
    const [status, setStatus] = useState(0);
    const [quizGroups, setQuizGroups] = useState([]);
    const [cloneModalOpen, setCloneModalOpen] = useState(false);
    const [confirmLoading, setConfirmLoading] = useState(false);
    const [quizzesToClone, setQuizzesToClone] = useState([]);
    const [quizType, setQuizType] = useState("");
    const [groups, setGroups] = useState([]);
    const [answerBySwitch, setAnswerBySwitch] = useState(false);
    const [editQuiz, setEditQuiz] = useState(true);
    const [quiz, setQuiz] = useState({
        id: null,
        followUpEventId: "",
        contractTypeId: null,
        isAnsweredByConsultant: 0,
        description: "",
        reference: "",
        status: 0,
        language: false,
    });

    useEffect(() => {
        document.title = `${appTitle} - Quiz Builder`;

        getQuizTypes();
        getContractTypesOptions();

        if (id !== undefined) {
            getQuiz(id);
            setEditQuiz(false);
        }
    }, []);

    useEffect(() => {
        handleCheckAllGroups();
    }, [groups]);


    const getQuiz = async (id) => {
        await getQuizById(id)
            .then((res) => {
                if (res.status === 200) {
                    handleRefreshPage(res.data);
                    setShowAddGroupButton(true);
                } else {
                    const errors = res.data.errors;

                    errors.forEach((error) => {
                        const errorMessage = error.message;
                        AdnMessageError(errorMessage);
                    });
                }
            })
            .catch(() => {
                AdnMessageError("Error retrieving quiz.");
            });
    };

    const quizBuilderBackButton = () => {
        navigate("/quizList/");
    };

    const onSaveQuiz = async (values) => {
        setEditQuiz(false);
        quiz.id
            ? handleUpdateQuiz(values)
            : handleAddQuiz(values);
    };

    const handleAddQuiz = async (values) => {
        await addQuiz({
            followUpEventId: values.followUpEventId,
            contractTypeId: values.contractTypeId,
            isAnsweredByConsultant: values.isAnsweredByConsultant,
            description: values.note ? values.note : "",
            reference: values.reference,
            status: status,
            language: values.language ? "EN" : "PT",
        })
            .then((res) => {
                if (res.status === 200) {
                    setQuiz({ ...quiz, id: res.data.id });
                    navigate(AppRoutes.quizBuilderById.createPath(res.data.id));
                    setShowAddGroupButton(true);
                    AdnMessageSuccess("Quiz saved with success.");
                    quizForm.setFieldValue("id", res.data.id);
                } else {
                    const errors = res.data.errors;

                    errors.forEach((error) => {
                        const errorMessage = error.message;
                        AdnMessageError(errorMessage);
                    });
                }
            })
            .catch(() => {
                AdnMessageError("Could not save quiz.");
            });
    };

    const handleUpdateQuiz = async (values) => {
        await updateQuiz({
            id: values.id,
            followUpEventId: values.followUpEventId,
            contractTypeId: values.contractTypeId,
            isAnsweredByConsultant: values.isAnsweredByConsultant,
            description: values.note ? values.note : "",
            reference: values.reference,
            status: status,
            language: values.language ? "EN" : "PT",
        })
            .then((res) => {
                if (res.status === 200) {
                    AdnMessageSuccess("Quiz updated with success.");
                } else {
                    const errors = res.data.errors;

                    errors.forEach((error) => {
                        const errorMessage = error.message;
                        AdnMessageError(errorMessage);
                    });
                }
            })
            .catch(() => {
                AdnMessageError("Error Updating quiz.");
            });
    };

    const onGetGroupsByQuizId = async (quizId, isDelete = false) => {
        await getGroupByQuizId(quizId)
            .then((res) => {
                if (res.status === 200) {
                    let groupList = [];
                    let group;
                    let groupIndex = -1;
                    res.data.forEach((element) => {
                        //Build the groups
                        if (!isGroupSubgroup(element.order)) {
                            groupIndex++;
                            element.subGroups = [];
                            group = element;
                            groupList[groupIndex] = element;
                        }
                        //Build the subgroups inside the group
                        else {
                            if (!groupList[groupIndex].subGroups) {
                                groupList[groupIndex].subGroups = [];
                            }
                            groupList[groupIndex].subGroups.push(element);
                        }
                    });
                    if (
                        isDelete &&
                        ((res.data.length === 1 && res.data.parentGroupId === undefined) ||
                            res.data.length === 0)
                    ) {
                        setAnswerBySwitch(false);
                    }
                    setGroups(groupList);
                    setShowAddGroupButton(true);
                } else {
                    const errors = res.data.errors;

                    errors.forEach((error) => {
                        const errorMessage = error.message;
                        AdnMessageError(errorMessage);
                    });
                }
            })
            .catch(() => {
                AdnMessageError("Error retrieving groups..");
            });
    };

    const getQuizTypes = async () => {
        await getQuizTypeList()
            .then((res) => {
                if (res.status === 200) {
                    setQuizTypes([...res.data]);
                } else {
                    const errors = res.data.errors;

                    errors.forEach((error) => {
                        const errorMessage = error.message;
                        AdnMessageError(errorMessage);
                    });
                }
            })
            .catch(() => {
                AdnMessageError("Error retrieving quiz types.");
            });
    };

    const handleQuizType = (value) => {
        setQuizType(value);
        setQuiz({ ...quiz, followUpEventId: value });
    };

    const handleQuizReference = (value) => {
        setReference(value.target.value);
        setQuiz({ ...quiz, reference: value.target.value });
    };

    const handleQuizLanguage = (value) => {
        setQuiz({ ...quiz, language: value ? "EN" : "PT" });
    };

    const handleIsAnsweredByConsultant = (value) => {
        setQuiz({ ...quiz, isAnsweredByConsultant: value });
    };

    const handleQuizDescription = (value) => {
        setQuiz({ ...quiz, description: value.target.value });
    };

    const handleContractType = (value) => {
        setQuiz({ ...quiz, contractTypeId: value });
    };

    const handlePublishQuiz = async (quiz) => {
        await publishQuiz(quiz)
            .then((res) => {
                if (res.status === 200) {
                    getQuiz(quiz.id);
                    AdnMessageSuccess("Quiz published with success.");
                } else {
                    const errors = res.data.errors;

                    errors.forEach((error) => {
                        const errorMessage = error.message;
                        AdnMessageError(errorMessage);
                    });
                }
            })
            .catch(() => {
                AdnMessageError("Failed publishing the quiz.");
            });
    };

    const handleRefreshPage = async (value) => {
        quizForm.setFieldValue("id", value.id);
        quizForm.setFieldValue("reference", value.reference);
        quizForm.setFieldValue("followUpEventId", value.followUpEventId);
        quizForm.setFieldValue("contractTypeId", value.contractTypeId);
        quizForm.setFieldValue(
            "note",
            typeof value.description !== "undefined" ? value.description : value.note
        );
        quizForm.setFieldValue("language", value.language === "EN");
        quizForm.setFieldValue(
            "isAnsweredByConsultant",
            value.isAnsweredByConsultant
        );

        setQuiz({ ...value });
        await onGetGroupsByQuizId(value.id);
        setCloneModalOpen(false);
        setGoButton(true);
        setConfirmLoading(false);
        navigate(AppRoutes.quizBuilderById.createPath(value.id));
    };

    const handleOnSelectQuizClone = async (id) => {
        await cloneQuiz({ id })
            .then((res) => {
                if (res.status === 200) {
                    navigate(AppRoutes.quizBuilderById.createPath(res.data.id));
                    getQuiz(res.data.id);
                    setCloneModalOpen(false);
                } else {
                    const errors = res.data.errors;

                    errors.forEach((error) => {
                        const errorMessage = error.message;
                        AdnMessageError(errorMessage);
                    });
                }
            })
            .catch(() => {
                AdnMessageError("Error cloning the quiz.");
            });
    };

    const handleQuizClone = async () => {
        setCloneModalOpen(true);
        await getQuizList({ followUpEventId: quiz.followUpEventId })
            .then((res) => {
                if (res.status === 200) {
                    setQuizzesToClone(res.data.result);
                } else {
                    const errors = res.data.errors;

                    errors.forEach((error) => {
                        const errorMessage = error.message;
                        AdnMessageError(errorMessage);
                    });
                }
            })
            .catch(() => {
                AdnMessageError("Error retrieving quizzes to clone.");
            });
    };

    const handleCloneModalOk = () => {
        setConfirmLoading(true);
        setTimeout(() => {
            setCloneModalOpen(false);
            setConfirmLoading(false);
        }, 2000);
    };
    const handleCloneModalCancel = () => {
        setCloneModalOpen(false);
    };

    const isGroupSubgroup = (value) => {
        let result = value - Math.floor(value) !== 0;

        if (result) return true;
        else return false;
    };

    const handleAddGroup = () => {
        const index = quizGroups.length + 1;
        let newGroup = (
            <ManageGroupPage
                index={index}
                key={index}
                isSubGroup={false}
                quizId={quiz.id}
                setShowAddGroupButton={setShowAddGroupButton}
                setShowPublishButton={setShowPublishButton}
                onGetGroupsByQuizId={onGetGroupsByQuizId}
                groups={groups}
                quiz={quiz}
                setAnswerBySwitch={setAnswerBySwitch}
            />
        );
        setQuizGroups([...quizGroups, newGroup]);
        setShowAddGroupButton(false);
    };

    const handleCheckAllGroups = () => {
        if (groups.length === 0) {
            setQuizGroups([]);
            return;
        }

        const newQuizGroups = groups.map((group, index) => (
            <ManageGroupPage
                key={group.id}
                index={index + 1}
                parentGroupId={group.id}
                isSubGroup={group.isGroup}
                group={group}
                quizId={quiz.id}
                subGroups={group.subGroups}
                setShowAddGroupButton={setShowAddGroupButton}
                setShowPublishButton={setShowPublishButton}
                onGetGroupsByQuizId={onGetGroupsByQuizId}
                groups={groups}
                quiz={quiz}
                setAnswerBySwitch={setAnswerBySwitch}
            />
        ));
        setQuizGroups(newQuizGroups);
    };

    const getContractTypesOptions = async () => {
        await getContractTypes()
            .then((res) => {
                if (res.status === 200) {
                    setContractTypeOptions([...res.data]);
                } else {
                    const errors = res.data.errors;

                    errors.forEach((error) => {
                        const errorMessage = error.message;
                        AdnMessageError(errorMessage);
                    });
                }
            })
            .catch(() => {
                AdnMessageError("Could not get quiz types.");
            });
    };

    return (
        <>
            <QuizBuilderForm
                quizForm={quizForm}
                onSaveQuiz={onSaveQuiz}
                title="Quiz Builder"
                quizTypes={quizTypes}
                quizGroups={quizGroups}
                showAddGroupButton={showAddGroupButton}
                setShowAddGroupButton={setShowAddGroupButton}
                handleQuizType={handleQuizType}
                handleQuizReference={handleQuizReference}
                handleQuizDescription={handleQuizDescription}
                handleQuizLanguage={handleQuizLanguage}
                quizType={quizType}
                quiz={quiz}
                reference={reference}
                handleQuizClone={handleQuizClone}
                cloneModalOpen={cloneModalOpen}
                confirmLoading={confirmLoading}
                handleCloneModalOk={handleCloneModalOk}
                handleCloneModalCancel={handleCloneModalCancel}
                quizzesToClone={quizzesToClone}
                showGoButton={showGoButton}
                groups={groups}
                setQuizGroups={setQuizGroups}
                quizBuilderBackButton={quizBuilderBackButton}
                handlePublishQuiz={handlePublishQuiz}
                showPublishButton={showPublishButton}
                setShowPublishButton={setShowPublishButton}
                handleAddGroup={handleAddGroup}
                handleOnSelectQuizClone={handleOnSelectQuizClone}
                handleIsAnsweredByConsultant={handleIsAnsweredByConsultant}
                answerBySwitch={answerBySwitch}
                setEditQuiz={setEditQuiz}
                editQuiz={editQuiz}
                handleContractType={handleContractType}
                contractTypeOptions={contractTypeOptions}
            />
        </>
    );
};

export default ManageQuizBuilderPage;
